import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  User, 
  ChevronRight,
  MapPin,
  IndianRupee,
  Sparkles,
  TrendingUp,
  Home
} from 'lucide-react';

// Blog articles data
const BLOG_ARTICLES = {
  'rajasthan-5-day-itinerary': {
    slug: 'rajasthan-5-day-itinerary',
    title: 'Best 5-Day Rajasthan Itinerary Planned by AI',
    subtitle: 'Jaipur, Udaipur & Jodhpur - The Royal Triangle',
    description: 'Experience the magic of Rajasthan with this AI-optimized 5-day itinerary covering the majestic cities of Jaipur, Udaipur, and Jodhpur.',
    image: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=800&q=80',
    author: 'Panchi AI',
    date: 'March 15, 2026',
    readTime: '8 min read',
    category: 'Itineraries',
    tags: ['Rajasthan', 'Jaipur', 'Udaipur', 'Jodhpur', '5 Days'],
    content: `
## Why Rajasthan?

Rajasthan, the Land of Kings, offers an unparalleled blend of history, culture, and architectural marvels. This AI-crafted itinerary takes you through the Golden Triangle's royal cities, optimized for the perfect balance of sightseeing, local experiences, and relaxation.

## Day 1: Arrive in Jaipur - The Pink City

**Morning:**
- Arrive at Jaipur International Airport
- Check into your hotel in the old city area (recommended: Zostel Jaipur for budget, Rambagh Palace for luxury)
- Freshen up and have a traditional Rajasthani breakfast

**Afternoon:**
- Visit the iconic **Hawa Mahal** (Palace of Winds) - Best photos in morning light
- Explore **City Palace** - A stunning blend of Rajasthani and Mughal architecture
- Walk through the colorful bazaars of Johari Bazar

**Evening:**
- Sunset at **Nahargarh Fort** - Panoramic views of the Pink City
- Dinner at **Tapri Central** - Rooftop cafe with amazing chai

**Budget:** ₹3,000-5,000 (including accommodation)

---

## Day 2: Jaipur - Forts & Culture

**Morning:**
- Early visit to **Amber Fort** (reach by 8 AM to avoid crowds)
- Optional: Elephant ride up to the fort
- Explore the Mirror Palace (Sheesh Mahal)

**Afternoon:**
- Visit **Jal Mahal** (Water Palace) - Photo stop
- Lunch at **Laxmi Mishthan Bhandar (LMB)** - Try the famous Dal Baati Churma
- Explore **Albert Hall Museum**

**Evening:**
- Shopping at **Bapu Bazaar** - Textiles, jewelry, handicrafts
- Attend a cultural show at **Chokhi Dhani**

**Budget:** ₹2,500-4,000

---

## Day 3: Jaipur to Udaipur - The City of Lakes

**Morning:**
- Depart for Udaipur (6-hour drive or 1-hour flight)
- Scenic route through Rajasthani countryside
- Stop at **Chittorgarh Fort** (optional, adds 2 hours)

**Afternoon:**
- Arrive in Udaipur and check into a lakeside hotel
- Rest and freshen up

**Evening:**
- Boat ride on **Lake Pichola** at sunset
- View of **Taj Lake Palace** from the water
- Dinner at **Ambrai Restaurant** - Lake views

**Budget:** ₹4,000-6,000 (including transport)

---

## Day 4: Udaipur - Romance & Royalty

**Morning:**
- Visit **City Palace** - Largest palace complex in Rajasthan
- Explore the crystal gallery and vintage car collection
- Walk through the old city lanes

**Afternoon:**
- Lunch at **Savage Garden** - Great rooftop views
- Visit **Jagdish Temple**
- Explore **Bagore Ki Haveli**

**Evening:**
- Cultural dance performance at Bagore Ki Haveli
- Sunset from **Monsoon Palace** (Sajjangarh)
- Romantic dinner by the lake

**Budget:** ₹2,000-3,500

---

## Day 5: Udaipur to Jodhpur - The Blue City

**Morning:**
- Early departure for Jodhpur (4-5 hours)
- Stop at **Ranakpur Jain Temple** - Architectural marvel

**Afternoon:**
- Arrive in Jodhpur, check into a haveli in the old city
- Lunch with views of **Mehrangarh Fort**
- Visit the magnificent fort - One of India's largest

**Evening:**
- Walk through the blue streets of the old city
- Sunset from **Pachetia Hill**
- Dinner at **Stepwell Cafe**
- Fly out from Jodhpur or continue your journey

**Budget:** ₹3,500-5,500

---

## Total Budget Breakdown

| Category | Budget | Mid-Range | Luxury |
|----------|--------|-----------|--------|
| Accommodation | ₹5,000 | ₹15,000 | ₹50,000+ |
| Transport | ₹4,000 | ₹8,000 | ₹15,000 |
| Food | ₹3,000 | ₹6,000 | ₹12,000 |
| Activities | ₹2,000 | ₹4,000 | ₹8,000 |
| **Total** | **₹14,000** | **₹33,000** | **₹85,000+** |

## Pro Tips from Panchi AI

1. **Best Time to Visit:** October to March (avoid summer heat)
2. **Book Heritage Hotels:** Experience authentic havelis for a royal stay
3. **Carry Cash:** Many local shops don't accept cards
4. **Dress Modestly:** Especially when visiting temples
5. **Bargain:** In bazaars, start at 50% of the quoted price

---

## Ready to Plan Your Rajasthan Trip?

Let Panchi AI create a personalized itinerary based on your preferences, budget, and travel dates. Our AI considers real-time events, weather, and local festivals to craft the perfect trip.
    `
  },
  'goa-budget-trip': {
    slug: 'goa-budget-trip',
    title: 'How to Plan a Goa Trip on a Budget Using AI',
    subtitle: 'Complete Guide: Under ₹15,000 Including Everything',
    description: 'The ultimate budget Goa guide. Plan an amazing beach vacation under ₹15,000 with AI-optimized recommendations for stays, food, and activities.',
    image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=800&q=80',
    author: 'Panchi AI',
    date: 'March 10, 2026',
    readTime: '10 min read',
    category: 'Budget Travel',
    tags: ['Goa', 'Budget', 'Beach', 'Backpacking'],
    content: `
## Can You Really Do Goa Under ₹15,000?

Absolutely! With smart planning and AI-powered recommendations, you can experience the best of Goa - beaches, nightlife, food, and culture - without burning a hole in your pocket. Here's how Panchi AI helped thousands of travelers do exactly that.

## The Budget Breakdown

| Category | Budget Allocation |
|----------|------------------|
| Transport (Round trip) | ₹4,000-6,000 |
| Accommodation (4 nights) | ₹3,000-4,000 |
| Food & Drinks | ₹3,000-4,000 |
| Activities & Rentals | ₹2,000-3,000 |
| **Total** | **₹12,000-15,000** |

---

## Getting There: Smart Transport Hacks

### By Train (Most Budget-Friendly)
- **From Mumbai:** Konkan Railway - ₹400-800 (Sleeper)
- **From Bangalore:** Vasco Express - ₹500-900
- **From Delhi:** Goa Express - ₹800-1,500

**Pro Tip:** Book 2-3 months in advance for best prices. Use Panchi to compare across IRCTC, MakeMyTrip, and Cleartrip.

### By Bus
- **From Mumbai:** ₹600-1,200 (overnight Volvo)
- **From Bangalore:** ₹800-1,500

### By Flight (If You Find Deals)
- Watch for flash sales on IndiGo, SpiceJet
- Prices can drop to ₹1,500-2,500 one way
- Panchi alerts you when prices drop!

---

## Where to Stay: Budget Accommodations

### North Goa (Party Scene)
1. **Zostel Goa, Anjuna** - ₹500-800/night (dorms)
2. **The Hosteller, Vagator** - ₹600-900/night
3. **Old Quarter Hostel, Fontainhas** - ₹700-1,000/night

### South Goa (Peaceful Beaches)
1. **Goan Roots, Palolem** - ₹400-600/night
2. **Cuba Beach Huts, Palolem** - ₹800-1,200/night
3. **Home stays in Agonda** - ₹500-800/night

**AI Tip:** Panchi suggests South Goa for budget travelers - fewer tourists, cheaper stays, better beaches!

---

## 4-Day Budget Itinerary

### Day 1: Arrive & Explore Old Goa
- Arrive in Goa (morning train/bus)
- Rent a scooter (₹300-400/day) - ESSENTIAL for budget travel
- Check into hostel in North Goa
- Explore **Old Goa churches** (free entry)
- Sunset at **Vagator Beach**
- Dinner at a local shack (₹200-300)

**Day Cost:** ₹1,500

### Day 2: Beach Hopping - North Goa
- **Anjuna Beach** - Morning swim
- **Chapora Fort** - Famous "Dil Chahta Hai" viewpoint
- Lunch at **Curlies** - Budget-friendly, great vibe
- **Baga Beach** - Water sports (bargain hard!)
- Night market or club hopping (Tito's has free entry on some nights)

**Day Cost:** ₹1,000-1,500

### Day 3: South Goa Paradise
- Ride to South Goa (1.5 hours scenic route)
- **Palolem Beach** - Crescent-shaped paradise
- Kayaking to **Butterfly Island** (₹500)
- Fresh seafood lunch (₹250-400)
- **Cola Beach** - Hidden gem, Instagram-worthy
- Stay overnight in beach hut

**Day Cost:** ₹1,500-2,000

### Day 4: Culture & Departure
- Morning yoga on the beach (often free)
- Visit **Fontainhas** - Latin Quarter, colorful streets
- Brunch at **Vinayak Family Restaurant** - Local favorite
- Last-minute shopping at **Mapusa Market** (Fridays)
- Depart

**Day Cost:** ₹800-1,000

---

## Best Budget Eats

| Place | Location | Must Try | Price |
|-------|----------|----------|-------|
| Ritz Classic | Panjim | Pork Vindaloo | ₹180 |
| Vinayak | Panjim | Fish Thali | ₹150 |
| Anand Bar | Anjuna | Chicken Cafreal | ₹200 |
| Fisherman's Wharf | South | Seafood Platter | ₹400 |
| Beach Shacks | Any | Fish Curry Rice | ₹200 |

---

## Money-Saving Hacks

1. **Travel Off-Season:** September-October (monsoon end) has 50% cheaper stays
2. **Rent Scooters, Not Cars:** ₹300/day vs ₹1,500/day
3. **Eat Local:** Shacks > Restaurants
4. **Share Rides:** Split scooter petrol with travel buddies
5. **Free Activities:** Beaches, forts, churches, sunset views
6. **Carry Cash:** Avoid ATM fees, get better bargains
7. **Water Sports Bargain:** Never pay the first price, aim for 50% off

---

## What to Skip (Tourist Traps)

- Casino packages (overpriced)
- Expensive beach clubs (same sunset, 10x price)
- Branded alcohol (local feni is cheaper and authentic!)
- Package tours (DIY with Panchi is cheaper)

---

## Ready to Plan Your Budget Goa Trip?

Let Panchi AI create your personalized Goa itinerary. Just enter your budget, and our AI will optimize every rupee - finding the best transport deals, budget stays, and local experiences.
    `
  },
  'panchi-vs-makemytrip': {
    slug: 'panchi-vs-makemytrip',
    title: 'Why Panchi is Better than MakeMyTrip for Trip Planning',
    subtitle: 'AI-Powered Planning vs Traditional Booking - A Detailed Comparison',
    description: 'Honest comparison between Panchi AI and MakeMyTrip. See why travelers prefer AI-powered personalized planning over traditional booking platforms.',
    image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80',
    author: 'Panchi Team',
    date: 'March 5, 2026',
    readTime: '6 min read',
    category: 'Comparison',
    tags: ['MakeMyTrip', 'Comparison', 'AI', 'Travel Planning'],
    content: `
## The Problem with Traditional Travel Booking

We've all been there. You want to plan a trip, so you open MakeMyTrip, Cleartrip, or Goibibo. You book a flight. Then separately search for hotels. Then Google "things to do in [destination]". Then create a spreadsheet. Then share with friends. Then change dates. Then start over.

**It's exhausting.**

MakeMyTrip is great for *booking*. But booking isn't *planning*.

---

## Enter Panchi: AI That Actually Plans Your Trip

Panchi doesn't just help you book - it **plans your entire trip** in seconds. Here's how they compare:

| Feature | MakeMyTrip | Panchi |
|---------|------------|--------|
| Flight/Hotel Booking | ✅ Yes | ✅ Yes (via partners) |
| Personalized Itinerary | ❌ No | ✅ AI-generated |
| Day-by-Day Planning | ❌ No | ✅ Detailed schedule |
| Budget Breakdown | ❌ No | ✅ Complete analysis |
| Local Recommendations | ❌ Generic lists | ✅ AI-personalized |
| Travel Buddy Finder | ❌ No | ✅ Built-in community |
| Real-time Events | ❌ No | ✅ IPL, festivals, more |
| Trip Collaboration | ❌ No | ✅ Share & edit together |
| Learning from Feedback | ❌ No | ✅ AI improves over time |

---

## Real Example: Planning a Rajasthan Trip

### With MakeMyTrip:
1. Search flights to Jaipur ✈️
2. Book hotel in Jaipur 🏨
3. Google "Jaipur itinerary" 🔍
4. Read 10 blog posts 📱
5. Make a Google Doc 📝
6. Figure out transport to Udaipur 🚗
7. Book another hotel 🏨
8. Repeat for Jodhpur...
9. Calculate total budget 🧮
10. Share with friends, get feedback, change dates, START OVER

**Time spent: 4-6 hours**

### With Panchi:
1. Tell AI: "5 days in Rajasthan, ₹30,000 budget, love history"
2. Get complete itinerary with:
   - Day-by-day schedule
   - Hotel recommendations (with booking links)
   - Transport between cities
   - Restaurant suggestions
   - Budget breakdown
   - Insider tips
3. Share with friends, edit together
4. Book everything in one place

**Time spent: 5 minutes**

---

## Why AI Planning is Superior

### 1. Personalization
MakeMyTrip shows you the same hotels and flights as everyone else. Panchi understands YOUR preferences:
- Are you a morning person or night owl?
- Do you prefer history or adventure?
- Budget backpacker or luxury traveler?
- Solo explorer or group fun?

The AI crafts itineraries specifically for YOU.

### 2. Real-Time Intelligence
Panchi knows:
- IPL matches happening during your dates
- Local festivals you shouldn't miss
- Weather patterns affecting activities
- Real-time events and exhibitions

MakeMyTrip just shows you prices.

### 3. The Travel Buddy Factor
Planning solo? Panchi connects you with:
- Travelers going to the same destination
- Group trip organizers
- Local travel communities

MakeMyTrip? It's a lonely booking form.

### 4. Continuous Learning
Every trip you plan with Panchi makes it smarter:
- Remember your preferences
- Learn from your feedback
- Improve recommendations over time

---

## But Wait, Can I Still Book on MakeMyTrip?

Absolutely! Panchi integrates with multiple booking platforms:
- **Flights:** Compares across MakeMyTrip, Cleartrip, ixigo
- **Hotels:** Shows prices from Booking.com, Agoda, OYO
- **Transport:** IRCTC, RedBus, Uber

We're not replacing booking platforms - we're adding **intelligence** on top of them.

---

## What Users Say

> "I used to spend hours on MakeMyTrip comparing options. Panchi gave me a complete Goa itinerary in 30 seconds. Game changer!" 
> — Priya S., Mumbai

> "The travel buddy feature helped me find 3 friends for my Ladakh trip. Could never do that on MakeMyTrip."
> — Rahul K., Delhi

> "Panchi suggested I visit during Pushkar Fair - something I'd never have known. Made my Rajasthan trip magical."
> — Sarah M., Bangalore

---

## The Verdict

| Use Case | Winner |
|----------|--------|
| Just need to book a flight | MakeMyTrip |
| Planning a complete trip | **Panchi** |
| Finding best hotel deals | Tie |
| Creating detailed itineraries | **Panchi** |
| Group trip coordination | **Panchi** |
| Last-minute bookings | MakeMyTrip |
| First-time to destination | **Panchi** |
| Business travel | MakeMyTrip |
| Leisure/Adventure travel | **Panchi** |

---

## Try It Yourself

The best way to understand Panchi's power is to experience it. Plan your next trip and see the difference.

**Free. No booking required. Just pure AI-powered planning.**
    `
  }
};

// Article list for blog index
const ARTICLE_LIST = Object.values(BLOG_ARTICLES);

// Individual Article Component
function ArticlePage({ slug }) {
  const navigate = useNavigate();
  const article = BLOG_ARTICLES[slug];
  
  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-teal-600 hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft size={20} />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <Home size={18} className="text-teal-600" />
            <span className="font-semibold text-gray-900">Panchi</span>
          </Link>
          <ChevronRight size={16} className="text-gray-400" />
          <Link to="/blog" className="text-gray-600 hover:text-teal-600">Blog</Link>
        </div>
      </nav>
      
      {/* Hero */}
      <header className="relative h-[400px] overflow-hidden">
        <img 
          src={article.image} 
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 bg-teal-500 text-white text-sm font-semibold rounded-full">
              {article.category}
            </span>
            <span className="text-white/80 text-sm flex items-center gap-1">
              <Clock size={14} /> {article.readTime}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2">{article.title}</h1>
          <p className="text-xl text-white/90">{article.subtitle}</p>
        </div>
      </header>
      
      {/* Article Meta */}
      <div className="max-w-4xl mx-auto px-4 py-6 flex items-center gap-6 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
            {article.author[0]}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{article.author}</p>
            <p className="text-sm text-gray-500">{article.date}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 ml-auto">
          {article.tags.map(tag => (
            <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>
      
      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-8">
        <div 
          className="prose prose-lg max-w-none prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-8 prose-h3:text-xl prose-a:text-teal-600 prose-strong:text-gray-900 prose-table:text-sm"
          dangerouslySetInnerHTML={{ 
            __html: article.content
              .replace(/^## /gm, '<h2>')
              .replace(/^### /gm, '<h3>')
              .replace(/\n\n/g, '</p><p>')
              .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
              .replace(/\|(.+)\|/g, (match) => {
                const cells = match.split('|').filter(c => c.trim());
                return `<tr>${cells.map(c => `<td class="border px-3 py-2">${c.trim()}</td>`).join('')}</tr>`;
              })
          }}
        />
      </article>
      
      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl p-8 text-center text-white">
          <Sparkles className="mx-auto mb-4" size={40} />
          <h2 className="text-2xl font-bold mb-2">Ready to Plan Your Trip?</h2>
          <p className="text-white/90 mb-6">Let Panchi AI create your perfect itinerary in seconds</p>
          <Link 
            to="/planning" 
            className="inline-block px-8 py-3 bg-white text-teal-600 font-bold rounded-full hover:shadow-lg transition-all"
          >
            Start Planning Free
          </Link>
        </div>
      </div>
      
      {/* Related Articles */}
      <div className="max-w-4xl mx-auto px-4 py-8 border-t border-gray-100">
        <h3 className="text-xl font-bold text-gray-900 mb-6">More from Panchi Blog</h3>
        <div className="grid md:grid-cols-2 gap-6">
          {ARTICLE_LIST.filter(a => a.slug !== slug).slice(0, 2).map(a => (
            <Link 
              key={a.slug} 
              to={`/blog/${a.slug}`}
              className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all"
            >
              <div className="aspect-video overflow-hidden">
                <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
              <div className="p-4">
                <span className="text-xs text-teal-600 font-semibold">{a.category}</span>
                <h4 className="font-bold text-gray-900 mt-1 group-hover:text-teal-600 transition-colors">{a.title}</h4>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// Blog Index Component
function BlogIndex() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center text-white font-bold">
              P
            </div>
            <span className="font-bold text-xl text-gray-900">Panchi</span>
          </Link>
          <Link 
            to="/planning" 
            className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-full hover:shadow-lg transition-all"
          >
            Plan Your Trip
          </Link>
        </div>
      </nav>
      
      {/* Header */}
      <header className="max-w-6xl mx-auto px-4 py-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            Panchi Travel Blog
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            AI-crafted travel guides, detailed itineraries, and insider tips for exploring India and beyond
          </p>
        </motion.div>
      </header>
      
      {/* Featured Article */}
      <section className="max-w-6xl mx-auto px-4 mb-12">
        <Link 
          to={`/blog/${ARTICLE_LIST[0].slug}`}
          className="group block relative h-[400px] rounded-2xl overflow-hidden"
        >
          <img 
            src={ARTICLE_LIST[0].image} 
            alt={ARTICLE_LIST[0].title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <span className="px-3 py-1 bg-teal-500 text-white text-sm font-semibold rounded-full">
              Featured
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-3 mb-2 group-hover:text-teal-300 transition-colors">
              {ARTICLE_LIST[0].title}
            </h2>
            <p className="text-white/80 max-w-2xl">{ARTICLE_LIST[0].description}</p>
            <div className="flex items-center gap-4 mt-4 text-white/70 text-sm">
              <span className="flex items-center gap-1"><Calendar size={14} /> {ARTICLE_LIST[0].date}</span>
              <span className="flex items-center gap-1"><Clock size={14} /> {ARTICLE_LIST[0].readTime}</span>
            </div>
          </div>
        </Link>
      </section>
      
      {/* Article Grid */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <h3 className="text-2xl font-bold text-gray-900 mb-8">Latest Articles</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {ARTICLE_LIST.map((article, index) => (
            <motion.div
              key={article.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link 
                to={`/blog/${article.slug}`}
                className="group block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-teal-600">{article.category}</span>
                    <span className="text-gray-300">•</span>
                    <span className="text-xs text-gray-500">{article.readTime}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{article.description}</p>
                  <div className="mt-4 flex items-center text-teal-600 text-sm font-semibold">
                    Read Article <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
      
      {/* Newsletter CTA */}
      <section className="bg-gradient-to-r from-teal-500 to-cyan-500 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <TrendingUp className="mx-auto mb-4 text-white" size={40} />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Get Travel Inspiration</h2>
          <p className="text-white/90 mb-6">Join 10,000+ travelers getting AI-crafted itineraries and tips</p>
          <Link 
            to="/planning" 
            className="inline-block px-8 py-3 bg-white text-teal-600 font-bold rounded-full hover:shadow-lg transition-all"
          >
            Start Planning Your Trip
          </Link>
        </div>
      </section>
    </div>
  );
}

// Main Blog Component
export default function Blog() {
  const { slug } = useParams();
  
  if (slug) {
    return <ArticlePage slug={slug} />;
  }
  
  return <BlogIndex />;
}
